import React, { useEffect, useState } from "react";
import Offcanvas from 'react-bootstrap/Offcanvas';
import { EliminarClinica, ListarClinicas } from "../../Service/Serive.clinica";
import ClinicaCreate from "./ClinicaCreate";
import usePermissions from "../../hook/usePermissions";
import useListaLocal from "../../hook/useListaLocal";
import DataTable from "../UI/DataTable/DataTable";
import { Celda, CeldaDoble } from "../UI/DataTable/piezas";
import MenuAcciones from "../UI/MenuAcciones";
import Boton from "../UI/Boton";
import Modal from "../UI/Modal/Modal";
import { useToast } from "../UI/Aviso";
import { CampoTexto } from "../UI/Campo";
import { IconoMas, IconoEditar, IconoEliminar, IconoBuscar } from "../UI/Iconos";

export default function Clinica({tabs}) {
  // RBAC-CUTOVER-07: Nuevo/Editar/Eliminar dependen de clinics.create/
  // clinics.edit/clinics.delete (backend ya los exige en clinica.routes.ts).
  const { hasPermission } = usePermissions();
  const [show, setShow] = useState(false);
  const [editClinica, setEditClinica] = useState(null);
  const [listClinica, setListClinica] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ping, setPing] = useState(false);
  const [porBorrar, setPorBorrar] = useState(null);
  const [borrando, setBorrando] = useState(false);
  const { mostrar } = useToast();
  const lista = useListaLocal(listClinica, { campos: ["nombre", "email", "representante", "direccion"] });

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const response = await ListarClinicas();  // 🔄 Obtener clínicas con datos de `clinicas_contifico`

      if (response) {
        setListClinica(response);
        setError(null);
      } else {
        // Antes, un fallo dejaba `loading` en true PARA SIEMPRE y la tabla
        // vacía: spinner eterno y ni una palabra. El servicio devuelve `null`
        // sin el motivo del servidor, así que aquí no se puede decir más.
        setListClinica([]);
        setError("No se pudo obtener el listado de clínicas.");
      }
      setLoading(false);
    })();
  }, [ping, tabs.clinicas]);

  const editarClinica = (clinica) => {
    // HM derivado (exposicion de credenciales Contifico): el backend ya NO
    // devuelve key_contifico/token_contifico (ver ListarClinicas) -- el
    // formulario de edicion arranca con esos campos vacios a proposito
    // (ClinicaCreate.js usa `contifico_configurado` para mostrar un
    // placeholder en vez de precargar el secreto).
    setEditClinica(clinica);
    handleShow();
  };

  const DeleteClinica = async (idclinica) => {
    setBorrando(true);
    const response = await EliminarClinica(idclinica);
    setBorrando(false);
    setPorBorrar(null);
    if (response) {
      setPing(!ping);
      mostrar({ tipo: "exito", texto: "Clínica eliminada con éxito" });
    } else {
      mostrar({ tipo: "error", texto: "Ocurrió un error en la operación. Por favor, intente nuevamente." });
    }
  };

  const columnas = [
    {
      key: "nombre",
      titulo: "Clínica",
      render: (c) => <CeldaDoble principal={c.nombre} secundario={c.direccion} />,
    },
    {
      // Quién responde por la clínica y cómo llamarle: es un solo dato.
      key: "representante",
      titulo: "Representante",
      ancho: 220,
      render: (c) => <CeldaDoble principal={c.representante} secundario={c.telefono} />,
    },
    { key: "email", titulo: "Email", ancho: 240, render: (c) => <Celda valor={c.email} /> },
    {
      key: "acciones",
      titulo: "",
      ancho: 60,
      alinear: "centro",
      render: (c) => {
        const acciones = [];
        if (hasPermission("clinics.edit")) {
          acciones.push({ label: "Editar", icono: IconoEditar, onClick: () => editarClinica(c) });
        }
        if (hasPermission("clinics.delete")) {
          acciones.push({ label: "Eliminar", icono: IconoEliminar, peligro: true, onClick: () => setPorBorrar(c) });
        }
        return acciones.length > 0 ? <MenuAcciones acciones={acciones} titulo={`Acciones de ${c.nombre}`} /> : null;
      },
    },
  ];

  return (
    <section>
      {hasPermission("clinics.create") && (
        <div className="d-flex justify-content-end mb-3">
          <Boton variante="primario" icono={IconoMas} onClick={handleShow} data-testid="clinica-crear">
            Crear Clínica
          </Boton>
        </div>
      )}

      <div className="mb-3" style={{ maxWidth: 380 }}>
        <CampoTexto
          etiqueta="Buscar clínica"
          icono={IconoBuscar}
          placeholder="Nombre, email, representante o dirección"
          value={lista.texto}
          onChange={(e) => lista.setTexto(e.target.value)}
          data-testid="clinica-buscar"
        />
      </div>

      <DataTable
        columnas={columnas}
        filas={lista.visibles}
        filaKey="idclinica"
        cargando={loading}
        error={error}
        onReintentar={() => setPing(!ping)}
        etiquetaTabla="Clínicas"
        hayFiltrosAplicados={lista.hayBusqueda}
        onLimpiarFiltros={lista.limpiar}
        textoVacioSinFiltros="Todavía no hay ninguna clínica registrada."
        paginacion={lista.paginacion}
      />

      {/* Eliminar una clínica tampoco pedía confirmación. */}
      <Modal
        abierto={Boolean(porBorrar)}
        titulo="Eliminar clínica"
        destructivo
        onCerrar={() => setPorBorrar(null)}
        secundario={{ label: "Cancelar" }}
        primario={{
          label: "Eliminar",
          cargando: borrando,
          onClick: () => DeleteClinica(porBorrar.idclinica),
        }}
      >
        <p>
          Vas a eliminar <b>{porBorrar?.nombre}</b>. Esta acción no se puede deshacer.
        </p>
      </Modal>

      <Offcanvas style={{ width: '50%' }} placement="end" show={show} onHide={handleClose}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Creación Clínica</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <ClinicaCreate
            setPing={setPing}
            ping={ping}
            handleClose={handleClose}
            show={show}
            editClinica={editClinica}  // 🔹 Pasar `editClinica` corregido
          />
        </Offcanvas.Body>
      </Offcanvas>
    </section>
  );
}
